'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Search, Calendar, Clock, ArrowRight, Tag, BookOpen, ChevronRight } from 'lucide-react';
import { blogPosts, type BlogPost } from '@/lib/site-data';

const categories = ['All', 'Technical Guide', 'Market Insight', 'Case Study', 'Business Strategy'];

export default function BlogClient() {
  const searchParams = useSearchParams();
  const initialCategory = searchParams.get('category') || 'All';
  const initialQuery = searchParams.get('q') || '';

  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [searchQuery, setSearchQuery] = useState(initialQuery);

  const filteredPosts = useMemo(() => {
    let result = [...blogPosts];

    if (activeCategory !== 'All') {
      result = result.filter((p) => p.category === activeCategory);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q)),
      );
    }

    return result.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [activeCategory, searchQuery]);

  const featuredPost = filteredPosts.find((p) => p.featured) || filteredPosts[0];
  const otherPosts = filteredPosts.filter((p) => p.slug !== featuredPost?.slug);

  return (
    <main className="relative min-h-screen pt-24 pb-20">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[var(--color-border)]">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-primary)]/5 via-transparent to-[var(--color-accent)]/5" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[var(--color-primary)]/10 blur-[120px] rounded-full" />
        <div className="relative max-w-7xl mx-auto px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/20 text-[var(--color-primary)] text-sm font-medium mb-6">
              <BookOpen size={16} />
              WFD Insights Blog
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Expert Insights for{' '}
              <span className="bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] bg-clip-text text-transparent">
                EV After-Sales
              </span>
            </h1>
            <p className="text-lg text-[var(--color-muted)] mb-8 leading-relaxed">
              Technical guides, market analysis, and business strategies for workshops and garages
              transitioning to new energy vehicles. Learn from real-world experience in the Middle East and Southeast Asia.
            </p>

            {/* Search bar */}
            <div className="relative max-w-xl">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-muted)]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full h-12 pl-12 pr-4 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl text-[var(--color-foreground)] placeholder:text-[var(--color-muted)] focus:outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/20 transition-all"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 py-8 border-b border-[var(--color-border)]">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                activeCategory === cat
                  ? 'bg-[var(--color-primary)] text-[var(--color-background)]'
                  : 'bg-[var(--color-surface)] text-[var(--color-muted)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-surface-light)] border border-[var(--color-border)]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Featured Post */}
        {featuredPost && (
          <section className="py-12">
            <Link href={`/blog/${featuredPost.slug}`} className="group block">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className={`relative aspect-[16/10] rounded-2xl overflow-hidden bg-gradient-to-br ${featuredPost.imageGradient || 'from-emerald-500/30 to-cyan-500/30'} border border-[var(--color-border)]`}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <BookOpen size={80} className="text-white/20" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 rounded-full bg-[var(--color-primary)] text-[var(--color-background)] text-xs font-semibold">
                      Featured
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="text-xs text-white/70">{featuredPost.category}</span>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-4 text-sm text-[var(--color-muted)] mb-4">
                    <span className="flex items-center gap-1.5">
                      <Calendar size={14} />
                      {new Date(featuredPost.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock size={14} />
                      {featuredPost.readTime}
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-4 group-hover:text-[var(--color-primary)] transition-colors">
                    {featuredPost.title}
                  </h2>
                  <p className="text-[var(--color-muted)] mb-6 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center gap-2 text-[var(--color-primary)] font-medium group-hover:gap-3 transition-all">
                    Read Article
                    <ArrowRight size={18} />
                  </div>
                </div>
              </div>
            </Link>
          </section>
        )}

        {/* Blog Grid */}
        <section className="py-12">
          <h3 className="text-2xl font-bold mb-8">
            {activeCategory === 'All' ? 'Latest Articles' : activeCategory}
            <span className="text-[var(--color-muted)] font-normal text-base ml-3">
              {otherPosts.length + (featuredPost ? 1 : 0)} articles
            </span>
          </h3>

          {otherPosts.length === 0 && !featuredPost ? (
            <div className="text-center py-20">
              <Search size={40} className="mx-auto text-[var(--color-muted)]/30 mb-4" />
              <h3 className="text-lg font-medium mb-2">No articles found</h3>
              <p className="text-[var(--color-muted)]">
                Try adjusting your search or category filter.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {otherPosts.map((post) => (
                <BlogCard key={post.slug} post={post} />
              ))}
            </div>
          )}
        </section>

        {/* Newsletter CTA */}
        <section className="py-16">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--color-primary)]/10 to-[var(--color-accent)]/10 border border-[var(--color-primary)]/20 p-8 md:p-12">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--color-primary)]/20 blur-[100px] rounded-full" />
            <div className="relative max-w-2xl">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Get the latest EV insights delivered to your inbox
              </h3>
              <p className="text-[var(--color-muted)] mb-6">
                Monthly newsletter with technical tips, market trends, and exclusive training resources.
                No spam, unsubscribe anytime.
              </p>
              <Link
                href="/#newsletter"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-[var(--color-background)] bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] hover:shadow-lg hover:shadow-[var(--color-glow-soft)] transition-all"
              >
                Subscribe Now
                <ChevronRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

function BlogCard({ post }: { post: BlogPost }) {
  return (
    <Link href={`/blog/${post.slug}`} className="group block">
      <article className="h-full flex flex-col bg-[var(--color-surface)] border border-[var(--color-border)] rounded-2xl overflow-hidden hover:border-[var(--color-primary)]/30 hover:shadow-lg hover:shadow-[var(--color-primary)]/5 transition-all">
        <div className={`relative aspect-video bg-gradient-to-br ${post.imageGradient || 'from-emerald-500/20 to-cyan-500/20'}`}>
          <div className="absolute inset-0 flex items-center justify-center">
            <BookOpen size={40} className="text-white/20" />
          </div>
          <div className="absolute top-3 left-3">
            <span className="px-2.5 py-1 rounded-full bg-black/60 text-white text-[11px] font-medium backdrop-blur-sm">
              {post.category}
            </span>
          </div>
        </div>
        <div className="p-5 flex flex-col flex-1">
          <div className="flex items-center gap-3 text-xs text-[var(--color-muted)] mb-3">
            <span className="flex items-center gap-1">
              <Calendar size={12} />
              {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {post.readTime}
            </span>
          </div>
          <h4 className="text-lg font-semibold mb-3 group-hover:text-[var(--color-primary)] transition-colors line-clamp-2">
            {post.title}
          </h4>
          <p className="text-sm text-[var(--color-muted)] mb-4 line-clamp-3 flex-1">
            {post.excerpt}
          </p>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {post.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 text-[11px] rounded-md bg-[var(--color-surface-light)] text-[var(--color-muted)]"
              >
                #{tag}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-1 text-sm text-[var(--color-primary)] font-medium group-hover:gap-2 transition-all">
            Read more
            <ArrowRight size={14} />
          </div>
        </div>
      </article>
    </Link>
  );
}
